import { graphql, useStaticQuery } from "gatsby";
import React, {
  ReactNode, useEffect, useState, UIEvent,
} from "react";
import classNames from "classnames";
import {
  CookieBanner,
  ISocialLinks,
  PreSignupFooter,
} from "@trussle/external-components";
import { Meta } from "../../components/Meta";
import { HubSpotChat } from "../../components/HubSpot/HubSpotChat";
import { trackPageView } from "../../helpers/analytics";
import {
  getReferrerFromCookie,
  setLandingParams,
} from "../../helpers/landingParams";
import { Segment } from "./Segment";
import { OptimizelySnippet } from "./Optimizely";
import { Partner, usePartnerContext } from "../../components/Partner";
import { uSwitchFooterStyles } from "./partners/uSwitch";
import "./styles.scss";
import { TrussleHeader } from "../../components/TrussleHeader";
import { IMenuItemFromContentful } from "../../components/TrussleHeader/types";
import {
  ILoggedInStatus,
  useGetLoggedInStatus,
  useLoadLoggedInStatus,
} from "../../components/LoggedInStatus";
import CookieConsent from "./CookieConsent";
import { PanelWrapper } from "../../components/PanelWrapper";

interface IProps {
  children: React.ReactNode;
  hideHeader?: boolean;
  pathname?: string;
  hideHamburger?: boolean;
  enableAbTesting?: boolean;
  hideFooter?: boolean;
  hideLogin?: boolean;
  showSmallFooter?: boolean;
  onCookieStatusChange?: (status: string) => void;
  betterV2?: boolean;
  snapScroll?: boolean;
  isCookieDismissed?: boolean;
  floatingButton?: ReactNode;
}
interface ILink {
  href: string;
  label: string;
}
interface ILinkColumn {
  columnHeader: string;
  links: ILink[];
}

const FOOTER_STYLE_MAPPER = {
  [Partner.U_SWITCH]: uSwitchFooterStyles,
  [Partner.EMMA]: {},
  [Partner.NONE]: {},
  [Partner.FORBES]: {},
  [Partner.BRITISH_HOME_SELLERS]: {},
};

const PARTNERS_WITHOUT_LOG_IN = [
  Partner.U_SWITCH,
  Partner.FORBES,
  Partner.BRITISH_HOME_SELLERS,
];

const signInButtonOptions = [
  {
    label: "My application",
    onClick: () => window.location.assign(`${AUTH_UI_URL}/login/confirmed`),
  },
  {
    label: "Sign out",
    onClick: () => window.location.assign(`${AUTH_UI_URL}/logout`),
  },
];

export const Layout: React.FunctionComponent<IProps> = ({
  children,
  hideHeader = false,
  pathname,
  hideHamburger,
  enableAbTesting = false,
  hideFooter = false,
  hideLogin = false,
  showSmallFooter = false,
  betterV2 = false,
  snapScroll = false,
  onCookieStatusChange,
  isCookieDismissed = false,
  floatingButton,
}) => {
  const [scrollTop, setScrollTop] = useState(0);
  const loadLoginStatus = useLoadLoggedInStatus();
  const loginStatus = useGetLoggedInStatus();
  const isLoggedIn = loginStatus === ILoggedInStatus.LOGGED_IN;
  const isLoading = loginStatus === ILoggedInStatus.LOADING;

  useEffect(() => {
    trackPageView();
    setLandingParams();
    if (loginStatus === ILoggedInStatus.UNKNOWN) {
      loadLoginStatus();
    }
  }, []);

  const referrer: string | undefined = getReferrerFromCookie();

  const {
    contentfulHeaderNavigationMenu: {
      globalNavigationMenuItems: globalNavigationMenu,
      headerMenuItemsV2,
    },
    contentfulFooterNavigationMenu: {
      footerMenuItems: footerNavigationMenu,
      footerMenuItemsBetterV2,
    },
  }: {
    contentfulHeaderNavigationMenu: {
      globalNavigationMenuItems: IMenuItemFromContentful[];
      headerMenuItemsV2: IMenuItemFromContentful[];
    };
    contentfulFooterNavigationMenu: {
      footerMenuItems: {
        navigationMenu?: ILinkColumn[];
        socialLinks: ISocialLinks;
      };
      footerMenuItemsBetterV2: {
        navigationMenu?: ILinkColumn[];
        socialLinks: ISocialLinks;
      };
    };
  } = useStaticQuery(graphql`
    query {
      contentfulHeaderNavigationMenu: contentfulGlobalSettings(
        headerNavName: { eq: "Global Site Setting -Header" }
      ) {
        globalNavigationMenuItems {
          text
          url
          childrenType
          subMenuItems {
            text
            url
          }
        }
        headerMenuItemsV2 {
          text
          url
          subMenuItems {
            text
            url
          }
        }
      }

      contentfulFooterNavigationMenu: contentfulGlobalSettings(
        footerNavName: { eq: "Global Site Setting -Footer" }
      ) {
        footerMenuItems {
          navigationMenu {
            links {
              href
              label
              class
              id
              type
            }
            columnHeader
          }
          socialLinks {
            linkedin
            facebook
            instagram
          }
        }
        footerMenuItemsBetterV2 {
          navigationMenu {
            links {
              href
              label
              class
              id
              type
            }
            columnHeader
          }
          socialLinks {
            tiktok
            linkedin
            facebook
            instagram
          }
        }
      }
    }
  `);

  const partner = usePartnerContext();
  const footerStyle = FOOTER_STYLE_MAPPER[partner];
  const showLogin = !PARTNERS_WITHOUT_LOG_IN.includes(partner);
  const smallFooterLinks = footerNavigationMenu.navigationMenu
    && footerNavigationMenu.navigationMenu
      .filter(
        (item) =>
          item.columnHeader === "About Better.co.uk" || item.columnHeader === "Company"
      )
      .map((item) => {
        const links = item.links.filter((link) => !link.label.includes("London"));
        return {
          ...item,
          links,
        };
      });

  const regularMenu = betterV2
    ? footerMenuItemsBetterV2.navigationMenu
    : footerNavigationMenu.navigationMenu;
  const navigationMenu = showSmallFooter ? smallFooterLinks : regularMenu;
  const handleScrollDesktop = (event: UIEvent<HTMLDivElement>) => {
    setScrollTop(event.currentTarget.scrollTop);
  };
  const handleScrollMobile = (event: Event) => {
    setScrollTop((event.target as Document)?.scrollingElement?.scrollTop || 0);
  };

  useEffect(() => {
    if (betterV2) {
      window.addEventListener("scroll", handleScrollMobile);
    }
    return () => {
      if (betterV2) {
        window.removeEventListener("scroll", handleScrollMobile);
      }
    };
  }, [betterV2]);

  return (
    <div
      className={classNames({
        "snapping-container": snapScroll,
      })}
      onScroll={betterV2 ? handleScrollDesktop : undefined}
    >
      {
        /* Cookie Consent MUST be the first script in the head of the page to allow for
        third party cookie and script blocking */
       }
      <CookieConsent />
      <OptimizelySnippet enableAbTesting={enableAbTesting} />
      <Segment />
      <Meta pathname={pathname} />
      {HUBSPOT_CHAT_ENABLED && <HubSpotChat />}
      { !COOKIE_CONSENT_MANAGEMENT_PLATFORM_ENABLED && (
      <CookieBanner
        themeClassName="global-theme-trussle-v2"
        onStatusChange={onCookieStatusChange}
      />
      ) }
      {!hideHeader && (
        <TrussleHeader
          classOverrides={{
            headerInner: "trussleHeader",
          }}
          partner={referrer}
          homeUrl="/"
          navigationMenu={betterV2 ? headerMenuItemsV2 : globalNavigationMenu}
          onLogInButtonClick={() =>
            window.location.assign(`${AUTH_UI_URL}/login`)}
          onGetStartedButtonClick={() => window.location.assign("/get-started")}
          showMenu={!hideHamburger}
          showLogin={hideLogin ? !hideLogin : showLogin}
          signInButtonOptions={signInButtonOptions}
          isLoggedIn={isLoggedIn}
          isLoading={isLoading}
          betterV2={betterV2}
          socialLinks={footerMenuItemsBetterV2?.socialLinks}
        />
      )}
      <main className="main">{children}</main>
      {floatingButton && (
      <div
        className={classNames(
          "floating-button",
          { "hide-floating-button": scrollTop <= 96 },
          { "cookie-banner-closed": isCookieDismissed },
        )}
      >
        {floatingButton}
      </div>
      )}

      {hideFooter ? null : (
        <PanelWrapper isBetterV2={betterV2} containerClassName="hide-in-percy" backgroundColour={showSmallFooter ? "white" : "green"}>
          <PreSignupFooter
            showSmallFooter={showSmallFooter}
            navigationMenu={navigationMenu}
            socialLinks={
              showSmallFooter
                ? footerNavigationMenu.socialLinks
                : footerMenuItemsBetterV2.socialLinks
            }
            className={classNames("hide-in-percy", "footer-styles", {
              "footer-styles-dark": !showSmallFooter,
            })}
            style={footerStyle}
            variant={!showSmallFooter ? "dark" : "default"}
            classOverrides={{
              footerContent: snapScroll ? "footer-styles-content" : "",
            }}
          />
        </PanelWrapper>
      )}
    </div>
  );
};
